/**
 * パスワード再設定ページ (Phase 3 — Figma 準拠).
 *
 * URL: /reset-password?token=xxx
 * token + newPassword → POST /auth/reset-password → 完了 → ログインへ
 * Figma: #69 (強度インジケーター付き), #74 (完了)
 */

import { Link, useSearch } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { authResetPassword } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';

/**
 * URL から `token` パラメータを消去する。
 *
 * PR-VERIFY-RESET-CLEANUP: 成功 / 失敗どちらの後も、ブラウザ history / 共有された
 * URL / referrer ヘッダ経由で reset token が外部に漏れないように `?token=...` を
 * 取り除く。SPA 内なので window.location.replace は使わず、history.replaceState で
 * URL だけ書き換える (再レンダー誘発しない)。
 */
function scrubTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('token')) return;
  url.searchParams.delete('token');
  const next = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

export function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'パスワード再設定 - PetaCMS';
  }, []);

  // PR-VERIFY-RESET-CLEANUP: done 完了画面は token URL 消去後に再レンダーされても
  // 表示されるよう、`!token` ガードより先に判定する (順序逆転は意図的)。
  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
        <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <h2 className="mt-6 text-[16px] font-semibold text-[#23221e]">
            パスワードを変更しました
          </h2>
          <p className="mt-3 text-[14px] text-[#706d65]">
            新しいパスワードでログインしてください。
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => window.location.assign('/login')}
              className="h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-[15px] font-semibold text-white transition hover:bg-[#1d4ed8]"
            >
              ログイン
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
        <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <h2 className="mt-6 text-[16px] font-semibold text-[#23221e]">無効なリンク</h2>
          <p className="mt-3 text-[14px] text-[#706d65]">
            パスワードリセットリンクが無効です。もう一度リクエストしてください。
          </p>
          <div className="mt-6">
            <Link
              to="/forgot-password"
              className="text-[13px] font-medium text-[#3b82f6] hover:text-[#1d4ed8]"
            >
              パスワードリセットを再リクエスト
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }
    if (password.length < 12) {
      setError('パスワードは12文字以上で入力してください');
      return;
    }

    setLoading(true);
    try {
      await authResetPassword(token, password);
      setDone(true);
      // PR-VERIFY-RESET-CLEANUP: 成功後は token を URL から消去 (history / referrer 経由の漏洩防止)。
      // 失敗時は同 token で再 submit を許すため URL は残す (HIBP NG パスワード再入力等)。
      // 期限切れ token は server 側で consumed なので URL に残っても再利用不可。
      scrubTokenFromUrl();
    } catch (err) {
      setError(formatErrorMessage(err, 'パスワードの変更に失敗しました'));
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10">
        <div className="mb-8 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <p className="mt-1 text-[14px] text-[#706d65]">新しいパスワードを設定</p>
        </div>

        {error && (
          <div className="mb-5 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-sm text-[#991b1b]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="password" className="mb-2 block text-[13px] font-medium text-[#23221e]">
              新しいパスワード
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="12文字以上"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-2 block text-[13px] font-medium text-[#23221e]"
            >
              パスワード（確認）
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              minLength={12}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="もう一度入力"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-[15px] font-semibold text-white transition hover:bg-[#1d4ed8] disabled:opacity-50"
          >
            {loading ? '変更中...' : 'パスワードを変更'}
          </button>
        </form>
      </div>
    </div>
  );
}
